import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { COMMERCE_CAPABILITIES, hasCommerceCapability } from '../../platform/commerceCapabilities';
import { approveCommerceOperation, getCommerceOperation, type CommerceOperation } from './commerceApi';
import { canCommerceApprove, operationNotice, operationStatusTone } from './commerceModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '覆核失敗';
}

export function CommerceApprovalPanel({
  operationId,
  onCompleted,
}: {
  operationId?: string;
  onCompleted?: (operation: CommerceOperation) => void;
}) {
  const { role } = useAuth();
  const readable = hasCommerceCapability(role, COMMERCE_CAPABILITIES.commerceRead);
  const approvable = canCommerceApprove(role);
  const [input, setInput] = useState(operationId ?? '');
  const [password, setPassword] = useState('');
  const [operation, setOperation] = useState<CommerceOperation | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (readable && operationId) {
      setInput(operationId);
      void load(operationId);
    }
    // load is intentionally driven only by a newly created operation id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId, readable]);

  async function load(id = input) {
    if (!readable || !id.trim()) return;
    setBusy(true);
    setErr('');
    try {
      setOperation(await getCommerceOperation(id.trim()));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!readable || !input.trim() || !password) return;
    setBusy(true);
    setErr('');
    try {
      const next = await approveCommerceOperation(input.trim(), password);
      setPassword('');
      setOperation(next);
      if (next.status === 'completed') onCompleted?.(next);
    } catch (e) {
      setPassword('');
      const message = errorText(e);
      try { setOperation(await getCommerceOperation(input.trim())); } catch { /* keep approval error */ }
      setErr(message);
    } finally {
      setBusy(false);
    }
  }

  const notice = operationNotice(operation);

  if (!readable) return null;

  return (
    <section className="card">
      <div className="section-head">
        <div>
          <h2 className="section-title">雙人覆核</h2>
          <span className="section-meta">不同管理員 + 密碼二次驗證；操作 24 小時後失效</span>
        </div>
        {operation && <span className={`pill ${operationStatusTone(operation)}`}>{operation.status}</span>}
      </div>
      <div className="commerce-query-row">
        <label className="adm-field" style={{ flex: 1 }}>
          <span className="adm-field-label text-secondary">操作 ID</span>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="cop-…" />
        </label>
        <button className="btn-secondary" disabled={busy || !input.trim()} onClick={() => void load()}>
          查詢
        </button>
      </div>
      {operation && (
        <dl className="detail-grid" style={{ marginTop: 'var(--spacing-4)' }}>
          <div><dt>動作</dt><dd className="adm-mono">{operation.action}</dd></div>
          <div><dt>提案者</dt><dd className="adm-mono">{operation.maker_id}</dd></div>
          <div><dt>核准者</dt><dd className="adm-mono">{operation.checker_id || '待不同管理員'}</dd></div>
          <div><dt>到期</dt><dd>{formatTimestampUs(operation.expires_at)}</dd></div>
          <div><dt>案件</dt><dd className="adm-mono">{operation.case_id}</dd></div>
          <div><dt>理由</dt><dd>{operation.reason}</dd></div>
        </dl>
      )}
      {notice && (
        <p className={`adm-action-result ${notice.tone}`} role={notice.tone === 'is-err' ? 'alert' : undefined}>
          {notice.text}
        </p>
      )}
      {operation?.status === 'pending' && approvable && (
        <div className="commerce-query-row" style={{ marginTop: 'var(--spacing-4)' }}>
          <label className="adm-field" style={{ flex: 1 }}>
            <span className="adm-field-label text-secondary">核准者密碼（不保存）</span>
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button className="btn-danger" disabled={busy || !password} onClick={() => void approve()}>
            核准並執行
          </button>
        </div>
      )}
      {!approvable && <p className="commerce-dc-note">目前角色可查看，但不可核准；請由 admin、platform-owner 或 security-officer 覆核。</p>}
      {err && <p className="adm-action-result is-err" role="alert">{err}</p>}
    </section>
  );
}
